import { useState } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa6';
import { FiCornerUpLeft } from 'react-icons/fi';
import { formatDate } from '../../../../utils/collateralFormatters';
import useLegalCaseCourtDecisions, { type AddCourtDecisionValue } from '../../../../hooks/useLegalCaseCourtDecisions';
import { CourtDecisionType, type CourtDecision } from '../../../../types/LegalCase';
import { groupDecisions } from './groupDecisions';
import LegalCaseCourtDecisionForm from './LegalCaseCourtDecisionForm';
import ConfirmDialog from './ConfirmDialog';

interface LegalCaseCourtDecisionsProps {
    caseId: string;
    initialDecisions?: CourtDecision[];
}

// Uma linha de decisão. `variant` distingue a decisão de origem do recurso aninhado (recuado,
// com uma marca de vínculo). `canAppeal` libera o botão "Recorrer": só para decisões de origem
// que ainda não têm um recurso (regra do negócio: um recurso por decisão).
const CourtDecisionItem = ({
    decision,
    variant,
    canAppeal,
    onAppeal,
    onRemove,
    disabled,
}: {
    decision: CourtDecision;
    variant: 'origin' | 'appeal' | 'orphan';
    canAppeal: boolean;
    onAppeal: () => void;
    onRemove: () => void;
    disabled: boolean;
}) => {
    const isNested = variant === 'appeal';
    return (
        <li
            className={`flex items-start justify-between gap-2 py-2 ${isNested
                ? 'pl-4 border-l-2 border-blue-200 dark:border-blue-800 ml-1'
                : 'border-b border-gray-100 dark:border-slate-700 last:border-0'}`}
        >
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    {isNested && (
                        <FiCornerUpLeft size={12} className="text-blue-500 dark:text-blue-400 flex-shrink-0" />
                    )}
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{decision.decisionTypeFormatted}</span>
                    {decision.decisionDate && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">{formatDate(decision.decisionDate)}</span>
                    )}
                    {variant === 'orphan' && (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                            Recurso
                        </span>
                    )}
                </div>
                {decision.summary && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5 whitespace-pre-wrap break-words">{decision.summary}</p>
                )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
                {canAppeal && (
                    <button
                        type="button"
                        onClick={onAppeal}
                        disabled={disabled}
                        title="Recorrer desta decisão"
                        className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors disabled:opacity-50"
                    >
                        <FiCornerUpLeft size={12} />
                    </button>
                )}
                <button
                    type="button"
                    onClick={onRemove}
                    disabled={disabled}
                    title="Remover decisão"
                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900 rounded-lg transition-colors disabled:opacity-50"
                >
                    <FaTrash size={11} />
                </button>
            </div>
        </li>
    );
};

// Seção "Decisões" do detalhe do caso. Lista as decisões judiciais (sentença, acórdão,
// liminar, homologação, despacho), da mais recente para a mais antiga. O recurso interposto
// aparece agrupado logo abaixo da decisão de que recorre (mesmo conjunto visual). É entidade
// independente da audiência (uma decisão pode nascer fora de audiência). A remoção passa por
// ConfirmDialog; remover uma decisão de origem remove o recurso vinculado junto (avisado no
// diálogo e cascateado no backend).
const LegalCaseCourtDecisions = ({ caseId, initialDecisions }: LegalCaseCourtDecisionsProps) => {
    const decisions = useLegalCaseCourtDecisions(caseId, initialDecisions);
    const [showForm, setShowForm] = useState(false);
    const [appealingId, setAppealingId] = useState<string | null>(null);
    const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);

    const { origins, appealByOrigin, orphans } = groupDecisions(decisions.decisions);

    // A decisão em vias de remoção tem um recurso vinculado? Muda a mensagem de confirmação.
    const pendingHasAppeal = pendingRemoval !== null && appealByOrigin.has(pendingRemoval);

    // Algum formulário aberto (nova decisão ou recurso): trava o botão "Recorrer" das outras
    // linhas para não trocar a decisão-alvo com um form já preenchido (o state do form não
    // pode ser reaproveitado para outra origem).
    const anyFormOpen = showForm || appealingId !== null;

    const openForm = () => {
        decisions.clearErrors();
        setAppealingId(null);
        setShowForm(true);
    };

    const openAppealForm = (decisionId: string) => {
        decisions.clearErrors();
        setShowForm(false);
        setAppealingId(decisionId);
    };

    const closeForm = () => {
        decisions.clearErrors();
        setShowForm(false);
        setAppealingId(null);
    };

    const submitDecision = async (value: AddCourtDecisionValue): Promise<boolean> => {
        const ok = await decisions.addDecision(value);
        if (ok) closeForm();
        return ok;
    };

    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        await decisions.removeDecision(pendingRemoval);
        setPendingRemoval(null);
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Decisões judiciais
                </h4>
                {!showForm && appealingId === null && (
                    <button
                        type="button"
                        onClick={openForm}
                        className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
                    >
                        <FaPlus size={10} /> Decisão
                    </button>
                )}
            </div>

            {decisions.decisions.length === 0 && !showForm && (
                <p className="text-sm text-gray-400 dark:text-gray-500">Nenhuma decisão registrada</p>
            )}

            {decisions.decisions.length > 0 && (
                <ul>
                    {origins.map((origin) => {
                        const appeal = appealByOrigin.get(origin.id);
                        return (
                            <li key={origin.id} className="list-none">
                                <ul>
                                    <CourtDecisionItem
                                        decision={origin}
                                        variant="origin"
                                        canAppeal={!appeal}
                                        onAppeal={() => openAppealForm(origin.id)}
                                        onRemove={() => setPendingRemoval(origin.id)}
                                        disabled={decisions.isSaving || anyFormOpen}
                                    />
                                    {appeal && (
                                        <CourtDecisionItem
                                            decision={appeal}
                                            variant="appeal"
                                            canAppeal={false}
                                            onAppeal={() => {}}
                                            onRemove={() => setPendingRemoval(appeal.id)}
                                            disabled={decisions.isSaving || anyFormOpen}
                                        />
                                    )}
                                </ul>
                            </li>
                        );
                    })}
                    {orphans.map((orphan) => (
                        <CourtDecisionItem
                            key={orphan.id}
                            decision={orphan}
                            variant="orphan"
                            canAppeal={false}
                            onAppeal={() => {}}
                            onRemove={() => setPendingRemoval(orphan.id)}
                            disabled={decisions.isSaving || anyFormOpen}
                        />
                    ))}
                </ul>
            )}

            {showForm && (
                <LegalCaseCourtDecisionForm
                    typeOptions={decisions.typeOptions}
                    errorFields={decisions.errorFields}
                    isSaving={decisions.isSaving}
                    onSubmit={submitDecision}
                    onCancel={closeForm}
                />
            )}

            {appealingId !== null && (
                // key por appealingId: remonta o form ao trocar de decisão-alvo, zerando o
                // state interno — os valores digitados para um recurso não vazam para outro.
                <LegalCaseCourtDecisionForm
                    key={appealingId}
                    typeOptions={decisions.typeOptions}
                    errorFields={decisions.errorFields}
                    isSaving={decisions.isSaving}
                    relatedDecisionId={appealingId}
                    onSubmit={submitDecision}
                    onCancel={closeForm}
                />
            )}

            <ConfirmDialog
                open={pendingRemoval !== null}
                title="Remover decisão"
                message={pendingHasAppeal
                    ? 'Esta decisão tem um recurso vinculado, que também será removido. Esta ação não pode ser desfeita.'
                    : 'Remover esta decisão do caso? Esta ação não pode ser desfeita.'}
                isBusy={decisions.isSaving}
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
            />
        </div>
    );
};

export default LegalCaseCourtDecisions;
